import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  themeColor,
  tabletMediaQuery,
  FullBleed,
  mobileMediaQuery,
} from '../../Theming';
import { getYear } from 'date-fns';
import Logos from './Components/Logos';
import BenefitLinks from './Components/BenefitLinks';
import CompanyLinks from './Components/CompanyLinks';
import Connect from './Components/Connect';
import ProductLinks from './Components/ProductLinks';

const StyledFooter = styled.footer`
  background: ${themeColor('darkBlue')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: ${themeColor('smokeyWhite')};
  overflow: hidden;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  ${FullBleed}

  ${mobileMediaQuery} {
    margin: 0 0;
  }

  & a {
    text-decoration: none;
    color: inherit;

    &:visited {
      color: inherit;
    }
    &:hover {
      color: inherit;
    }
  }
`;

const Top = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 0.75rem;
  margin: 0 6.25vw;
  height: 485px;
  padding-top: 60px;

  ${tabletMediaQuery} {
    flex-direction: column;
    height: auto;
    margin: 0 48px;
    padding-top: 30px;
  }
`;

const Separator = styled.div`
  height: 1px;
  width: 100%;
  background: ${themeColor('grey')};
  opacity: 0.5;
`;

const Column = styled.section`
  display: flex;
  flex-direction: column;
  margin: 15px 10px;

  &:first-child {
    margin-left: 0;
  }

  ${tabletMediaQuery} {
    margin: 15px 0;
  }
`;

const Company = styled(Column)`
  flex-shrink: 0;
`;

const ConnectContainer = styled(Column)`
  display: flex;
  flex-direction: column;
  flex-grow: 5;
  flex-shrink: 1;
`;

const SolutionsContainer = styled(Column)`
  flex-shrink: 0;
  flex-grow: 1;
  margin-right: 75px;
`;

const Bottom = styled.div`
  height: 80px;
  margin: 0 6.25vw;
  display: flex;
  align-items: center;

  ${tabletMediaQuery} {
    margin: 0 20px 20px;
    height: auto;
    flex-direction: row-reverse;
  }
`;

const Copyright = styled.div`
  font-size: 0.8125rem;
  line-height: 0.9375rem;
  flex: 1;
  display: flex;
  align-items: center;

  ${tabletMediaQuery} {
    order: 2;
    font-size: 0.4375rem;
  }
`;

const LogosContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  margin-right: 50px;

  ${tabletMediaQuery} {
    order: 1;
    justify-content: space-evenly;
    flex-wrap: wrap;
    margin: 10px 0;
  }
`;

const Footer: React.FC = () => {
  const { t } = useTranslation();

  return (
    <StyledFooter>
      <Top>
        <ConnectContainer>
          <Connect />
        </ConnectContainer>
        <SolutionsContainer>
          <ProductLinks />
        </SolutionsContainer>
        <SolutionsContainer>
          <BenefitLinks />
        </SolutionsContainer>
        <Company>
          <CompanyLinks />
        </Company>
      </Top>
      <Separator />
      <Bottom>
        <Copyright>
          {t('legal.copyright', { year: getYear(new Date()) })}
        </Copyright>
        <LogosContainer>
          <Logos />
        </LogosContainer>
      </Bottom>
    </StyledFooter>
  );
};

export default Footer;
